import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/utils';

interface ErrorStateProps {
  /** Custom icon to display. Defaults to ErrorStateIcons.Alert */
  icon?: React.ReactNode;
  /** Main error title */
  title: string;
  /** Descriptive message for the user */
  description?: string;
  /** Custom action element. If not provided, shows a retry button */
  action?: React.ReactNode;
  /** Callback for default retry action. Only used if action is not provided */
  onRetry?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Reusable error state component for displaying errors in a user-friendly way.
 * API is similar to EmptyState for consistency.
 */
export function ErrorState({
  icon = ErrorStateIcons.Alert,
  title,
  description,
  action,
  onRetry,
  className,
  size = 'md',
}: ErrorStateProps) {
  const sizeClasses = {
    sm: {
      container: 'py-8 px-6 space-y-3',
      iconContainer: 'w-16 h-16',
      icon: 'size-8',
      title: 'text-base',
      description: 'text-sm',
    },
    md: {
      container: 'py-12 px-8 space-y-4',
      iconContainer: 'w-24 h-24',
      icon: 'size-12',
      title: 'text-lg',
      description: 'text-base',
    },
    lg: {
      container: 'py-16 px-10 space-y-6',
      iconContainer: 'w-32 h-32',
      icon: 'size-16',
      title: 'text-xl',
      description: 'text-lg',
    },
  };

  const classes = sizeClasses[size];

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const defaultAction = (
    <Button variant="outline" onClick={handleRetry} className="gap-2">
      <RefreshCw className="h-4 w-4" />
      Try again
    </Button>
  );

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        classes.container,
        className
      )}
    >
      {icon && (
        <div className="relative">
          <div
            className={cn(
              'rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center',
              classes.iconContainer
            )}
          >
            <div className={cn('text-red-500', classes.icon)}>{icon}</div>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <h3 className={cn('font-semibold text-gray-900', classes.title)}>{title}</h3>
        {description && (
          <p className={cn('text-gray-500 max-w-sm', classes.description)}>{description}</p>
        )}
      </div>
      <div className="pt-2">{action ?? defaultAction}</div>
    </div>
  );
}

// Predefined icon components for common error use cases
export const ErrorStateIcons = {
  Alert: (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  Network: (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
      />
    </svg>
  ),
  Server: (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
      />
    </svg>
  ),
  Lock: (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  ),
  NotFound: (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};
